export const minWidthArray: readonly [number, number, number, number] = [1920, 1200, 992, 100];

const buildQuery = (value: number) => `@media (min-width: ${value}px)`;

export type SizeLabel = 'extrasmall' | 'small' | 'medium' | 'large';

export const breakpoints: Record<SizeLabel, string> = {
  large: buildQuery(minWidthArray[0]),
  medium: buildQuery(minWidthArray[1]),
  small: buildQuery(minWidthArray[2]),
  extrasmall: buildQuery(minWidthArray[3]),
};

export const breakpointMap: Record<SizeLabel, number> = {
  large: minWidthArray[0],
  medium: minWidthArray[1],
  small: minWidthArray[2],
  extrasmall: minWidthArray[3],
};

// ——— Mobile layer (max-width side) ———
// The min-width `breakpoints` above are desktop enhancements layered on an
// un-suffixed base; `mq` is their additive complement for phone/tablet fixes.
// Every mobile declaration in the app must live inside one of these blocks so
// it is provably unreachable at >=992px CSS viewports.
// - `screen and` is load-bearing: without a media TYPE, printing/Save-as-PDF
//   evaluates `width` against the page box (~720-816px) and the whole mobile
//   layer would apply to desktop PRINT output. Never drop it.
// - Fractional stops (the N-0.02 convention) guarantee no viewport width
//   matches BOTH `breakpoints.small` (min-width: 992px) and `mq.mobile` —
//   e.g. a 991.5px zoomed viewport. Never "simplify" 991.98 to 992: that
//   creates a band where both desktop and mobile rules fire at once.
const buildMaxQuery = (value: number) => `@media screen and (max-width: ${value - 0.02}px)`;

export type MobileSizeLabel = 'mobile' | 'phone';

export const mq: Record<MobileSizeLabel, string> = {
  // Primary tier: ALL mobile + tablet fixes. Complement of breakpoints.small,
  // derived from the same array entry so the two can never drift.
  mobile: buildMaxQuery(minWidthArray[2]), // '@media screen and (max-width: 991.98px)'
  // Finer phone-only adjustments. Use sparingly.
  phone: buildMaxQuery(768), // '@media screen and (max-width: 767.98px)'
};
